import { useCallback, useMemo, useState } from 'react';
import { Text } from 'react-native';
import {
  Flex,
  IconChevronRight,
  RemoteImage,
  Typography,
} from '@banhala/ui-kit';
import type {
  PromotionShortBannerProps,
  PromotionShortBannerEntity,
} from './types';
import { bridge } from '../../../../utils';
import { useForegroundInterval, useOpenDeeplink } from '../../../../hooks';
import type { IScreensLogging } from '../../../../api/types/IScreenLogging';

export const PromotionShortBanner = ({
  entity,
  logging,
}: PromotionShortBannerProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useForegroundInterval(
    () => {
      if (!entity || entity.length <= 1) return;

      setCurrentIndex(prev => (prev + 1) % entity.length);
    },
    !entity || entity.length <= 1 ? 0 : 3000,
  );

  if (!entity || entity.length === 0) return null;

  const current = entity[currentIndex];

  return (
    <Flex padding={{ horizontal: 16 }}>
      <BannerItem entity={current} logging={logging} />
    </Flex>
  );
};

const BannerItem = ({
  entity,
  logging,
}: {
  entity: PromotionShortBannerEntity;
  logging: IScreensLogging;
}) => {
  const { item } = entity;
  const { openDeeplink } = useOpenDeeplink();

  const handlePress = useCallback(() => {
    bridge.logAnalyticsEvent({
      name: 'CLICK_SHORT_BANNER',
      analytics: { ...logging?.analytics, ...entity.logging?.analytics },
      ably_only_log: {
        ...logging?.ably_only_log,
        ...entity.logging?.ably_only_log,
      },
    });
    openDeeplink(item.deeplink);
  }, [item.deeplink, logging, entity.logging, openDeeplink]);

  const timerText = useTimerText(item.timer?.expired_at);

  return (
    <Flex.Row
      height={32}
      borderRadius={4}
      alignItems="center"
      justifyContent="center"
      padding={{ horizontal: 8 }}
      gap={4}
      onPress={handlePress}
      backgroundColorValue={item.background_color}
    >
      {item.icon.length > 0 && (
        <RemoteImage
          uri={item.icon}
          width={20}
          height={20}
          resizeMode="contain"
        />
      )}
      <Typography type="subtitle4" color="content_primary" numberOfLines={1}>
        {item.text}
      </Typography>
      {timerText != null && item.timer?.text_color != null && (
        <Text
          style={{
            color: item.timer.text_color,
            fontSize: 12,
            fontWeight: '600',
          }}
        >
          {timerText}
        </Text>
      )}
      <IconChevronRight size={12} fill={item.arrow_color} />
    </Flex.Row>
  );
};

const useTimerText = (expiredAt?: string): string | null => {
  const [now, setNow] = useState(Date.now());

  useForegroundInterval(
    () => {
      setNow(Date.now());
    },
    !expiredAt ? 0 : 1000,
  );

  return useMemo(() => {
    if (!expiredAt) return null;
    const diff = Math.max(0, new Date(expiredAt).getTime() - now);

    const hours = Math.floor(diff / (1000 * 60 * 60));
    if (hours >= 24) {
      const days = Math.floor(hours / 24);
      return `${days}일`;
    }
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')} 남음`;
  }, [expiredAt, now]);
};
